import { useMutation, UseMutationOptions, useQuery } from "react-query";
import { isEmpty } from "lodash";
import { Property } from "components/groups/types";
import { fetchApi } from "helpers/reactQueryApi";

export const appsApiBaseUrl = () => "/api/apps";
export const appUrl = ({ externalAppId, appId }) => {
  if (isEmpty(externalAppId) && isEmpty(appId))
    throw new Error("appId or externalAppId required to build appUrl");

  return [
    appsApiBaseUrl(),
    "apps",
    externalAppId ? `external/${externalAppId}` : appId,
  ].join("/");
};

export const itemUrl = ({ appId, externalAppId, itemId, externalItemId }) => {
  if (isEmpty(itemId) && isEmpty(externalItemId))
    throw new Error("itemId or externalItemId required to build itemUrl");

  return [
    appUrl({ appId, externalAppId }),
    "items",
    externalItemId ? `external/${externalItemId}` : itemId,
  ].join("/");
};

type AppPayload = {
  collection_layout: {
    layout: "table" | "list" | "wall";
    options: {
      table: { stage: boolean };
      list: {
        stage: boolean;
        teaser: {
          body_template: string | undefined;
        };
      };
      wall: { stage: boolean };
    };
  };
  id: string;
  name: string;
  title_property: string;
  properties: Property[];
};

type LoadAppArgs = { appId?: string; externalAppId?: string };

export const useLoadApp = ({ appId, externalAppId }: LoadAppArgs, options?) =>
  useQuery<AppPayload>(appUrl({ appId, externalAppId }), options);

type ItemPayload = {
  id: string;
  values: {
    [key: string]:
      | undefined
      | string
      | { [key: string]: string }
      | { [key: string]: string }[];
  };
  display_values: object;
};

type LoadItemArgs = {
  appId?: string;
  externalAppId?: string;
  itemId?: string;
  externalItemId?: string;
};
export const useLoadItem = ({
  appId,
  externalAppId,
  itemId,
  externalItemId,
}: LoadItemArgs) => {
  const isEnabled = !!externalItemId || !!itemId;

  return useQuery<ItemPayload>(
    isEnabled ? itemUrl({ appId, externalAppId, itemId, externalItemId }) : "",
    {
      enabled: isEnabled,
    },
  );
};

type UpdateItemArgs = {
  appId?: string;
  externalAppId?: string;
  itemId?: string;
  externalItemId?: string;
  body: { values: { [key: string]: any } };
};
export function useUpdateItem(
  options?: UseMutationOptions<unknown, Error, UpdateItemArgs>,
) {
  return useMutation(
    ({ appId, externalAppId, itemId, externalItemId, body }: UpdateItemArgs) =>
      fetchApi(itemUrl({ appId, externalAppId, itemId, externalItemId }), {
        method: "PUT",
        body,
      }),
    options,
  );
}

type CreateItemArgs = {
  appId?: string;
  externalAppId?: string;
  body: { values: { [key: string]: any } };
  group: any;
};
export function useCreateItem(
  options?: UseMutationOptions<{ id: string }, Error, CreateItemArgs>,
) {
  return useMutation(
    ({ appId, externalAppId, body }: CreateItemArgs) =>
      fetchApi(`${appUrl({ appId, externalAppId })}/items`, {
        method: "POST",
        body,
      }),
    options,
  );
}
